/**
 * Leviathan Web - Form and API validation error plumbing
 * Quasar Prime LLC
 *
 * ONE place that turns an API failure into something a form can show.
 *
 * The API has always returned enough to point at the offending field:
 *
 *   {
 *     success: false,
 *     error: {
 *       code: 'VALIDATION_ERROR',
 *       message: 'Validation failed',
 *       details: { issues: [{ path: 'contactEmail', message: 'Invalid email address' }] }
 *     }
 *   }
 *
 * Every caller threw `issues` away and showed the top-level `message`, so the
 * operator saw "Validation failed" and had to guess which of thirty fields was
 * wrong. Worse, several callers did `setError(data.message || data.error)` —
 * and since this body has NO top-level `message`, that stored the `error`
 * OBJECT in React state. Rendering an object as a child makes React throw,
 * which unmounts the entire application: the white screen with no way back.
 *
 * So there are two rules here, and both matter:
 *   1. Never hand a non-string to `setError`. `readApiFailure` cannot return
 *      one — `message` is typed `string` and every branch produces one.
 *   2. Keep `issues` and give them back keyed by field path, so a form can
 *      show the message beside the input instead of in a banner at the top.
 */

/** Field path (as the API reports it) to the message for that field. */
export type FieldErrors = Record<string, string>;

export interface ApiFailure {
  /** Always a string, always safe to render. */
  message: string;
  /** Empty when the failure was not a per-field validation error. */
  fieldErrors: FieldErrors;
  status: number;
}

/**
 * Labels that do not survive mechanical de-camel-casing.
 * Keyed by the LAST segment of the path.
 */
const LABEL_OVERRIDES: Record<string, string> = {
  poNumber: 'PO Number',
  zip: 'ZIP',
  deliveryZip: 'Delivery ZIP',
  billingZip: 'Billing ZIP',
  sku: 'SKU',
  fein: 'FEIN',
  bolNumber: 'BOL Number',
  customerLocationId: 'Ship-To Location',
  productId: 'Product',
  unitPrice: 'Unit Price',
  taxRate: 'Tax Rate',
  isActive: 'Active',
};

function titleCaseSegment(segment: string): string {
  const override = LABEL_OVERRIDES[segment];
  if (override) return override;

  // customerId -> Customer. An "Id" suffix is plumbing; the operator picked a
  // thing from a dropdown, they did not type an identifier.
  const base = segment.length > 2 && segment.endsWith('Id') ? segment.slice(0, -2) : segment;

  return base
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * `items.0.quantity` -> `Item 1 - Quantity`.
 *
 * The index is rendered one-based because it is being read by a person
 * counting rows down a table, not by a programmer reading an array.
 */
export function humanizeFieldPath(path: string): string {
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return 'This field';

  const parts: string[] = [];
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i]!;
    if (/^\d+$/.test(segment)) {
      const previous = parts.pop();
      const singular = previous ? previous.replace(/s$/, '') : 'Item';
      parts.push(`${singular} ${Number(segment) + 1}`);
      continue;
    }
    parts.push(titleCaseSegment(segment));
  }
  return parts.join(' - ');
}

interface RawIssue {
  path?: unknown;
  message?: unknown;
}

function issuesFrom(body: unknown): RawIssue[] {
  if (typeof body !== 'object' || body === null) return [];
  const error = (body as Record<string, unknown>).error;
  if (typeof error !== 'object' || error === null) return [];
  const details = (error as Record<string, unknown>).details;
  if (typeof details !== 'object' || details === null) return [];
  const issues = (details as Record<string, unknown>).issues;
  return Array.isArray(issues) ? (issues as RawIssue[]) : [];
}

/**
 * Pull `details.issues[]` into a `{ path: message }` map.
 *
 * When two issues land on the same path the first wins — Zod reports them in
 * schema order, and the first is the one that describes the shape of the
 * problem rather than a consequence of it.
 */
export function extractFieldErrors(body: unknown): FieldErrors {
  const fieldErrors: FieldErrors = {};
  for (const issue of issuesFrom(body)) {
    const path = typeof issue.path === 'string' ? issue.path : '';
    const message = typeof issue.message === 'string' ? issue.message : '';
    if (!path || !message) continue;
    if (fieldErrors[path] === undefined) fieldErrors[path] = message;
  }
  return fieldErrors;
}

/**
 * A sentence a person can act on, built from whatever the body actually
 * contains. Guaranteed to be a string.
 */
export function extractMessage(body: unknown, fallback: string): string {
  if (typeof body === 'string' && body.trim()) return body;

  const fieldErrors = extractFieldErrors(body);
  const paths = Object.keys(fieldErrors);
  if (paths.length > 0) {
    // Name the fields. "Validation failed" is true and useless; "Contact Email:
    // Invalid email address" is what the operator needs in order to fix it.
    const named = paths
      .slice(0, 3)
      .map((path) => `${humanizeFieldPath(path)}: ${fieldErrors[path]}`)
      .join('; ');
    const remaining = paths.length - 3;
    return remaining > 0 ? `${named} (and ${remaining} more)` : named;
  }

  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;

    const error = record.error;
    if (typeof error === 'object' && error !== null) {
      const nested = (error as Record<string, unknown>).message;
      if (typeof nested === 'string' && nested.trim()) return nested;
    }
    // Some endpoints return { error: 'a string' }.
    if (typeof error === 'string' && error.trim()) return error;

    if (typeof record.message === 'string' && record.message.trim()) return record.message;
  }

  return fallback;
}

/**
 * Read a failed `fetch` Response into something a form can render.
 *
 * Never throws and never returns a non-string message, including when the body
 * is empty, is HTML from a proxy, or is not JSON at all.
 */
export async function readApiFailure(
  response: Response,
  fallback = 'Request failed'
): Promise<ApiFailure> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const status = response.status;
  const fieldErrors = extractFieldErrors(body);
  const statusFallback =
    status === 0 || Number.isNaN(status)
      ? fallback
      : `${fallback} (HTTP ${status}${response.statusText ? ` ${response.statusText}` : ''})`;

  return {
    message: extractMessage(body, statusFallback),
    fieldErrors,
    status,
  };
}

/**
 * Scroll to and focus the first field on the page that has an error.
 *
 * DOM order, not object order: "the first one that failed" means the first one
 * the operator will reach reading DOWN the page, and the API reports issues in
 * schema order, which is not the same thing.
 *
 * Fields opt in by carrying `data-field="<path>"`, matching the path the API
 * uses, so the wiring is one attribute per input.
 */
export function focusFirstFieldError(fieldErrors: FieldErrors): void {
  if (typeof document === 'undefined') return;
  if (Object.keys(fieldErrors).length === 0) return;

  const candidates = Array.from(document.querySelectorAll<HTMLElement>('[data-field]'));
  const target = candidates.find((element) => {
    const path = element.getAttribute('data-field');
    return path !== null && fieldErrors[path] !== undefined;
  });

  if (!target) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Focus after the scroll is queued; focusing first would make the browser
  // jump instantly and undo the smooth scroll.
  window.setTimeout(() => {
    try {
      target.focus({ preventScroll: true });
    } catch {
      /* a non-focusable element is fine — it has still been scrolled to */
    }
  }, 0);
}
